import {
  decideMumHubAccess,
  MUM_HUB_ALLOWLIST_DEFAULT,
  MUM_HUB_ALLOWLIST_KEY,
  MUM_HUB_FETCH_TTL_DEV_MS,
  MUM_HUB_FETCH_TTL_MS,
  MUM_HUB_FLAG_DEFAULT,
  MUM_HUB_FLAG_KEY,
  parseMumHubAllowlist,
} from "@cch/shared/mum-hub-remote-config";

/**
 * Firebase Remote Config on the WEBSITE (MAR-235).
 *
 * The app has gated Mum Hub on two Remote Config parameters since MAR-138,
 * and the website has not: its gate knew only the build flag, the browser
 * override, an entitlement and a due date (MAR-219). So Adrian's tester, who
 * is in the `mum_hub_allowlist` array, saw the tab on her phone and the
 * not-found page on capsulechichub.com. This module is the missing half — the
 * SAME two parameters, read the same way, deciding the same thing.
 *
 * The decision itself is not here: it is in `@cch/shared/mum-hub-remote-config`
 * alongside the parameter names, the defaults and the TTL, pinned against the
 * app's own copy by a parity test. This file is only the browser edge: the
 * env, the lazy SDK load, and one fetch per page load.
 *
 * ## The SDK is not in the bundle unless it can be used
 *
 * `firebase/app` plus `firebase/remote-config` is a real amount of JavaScript
 * to hand every visitor of a page they will never see. So:
 *
 * - the three `VITE_FIREBASE_*` variables are read FIRST, synchronously, with
 *   no import at all. Absent or blank, this module answers "no" and the
 *   dynamic `import()` never runs, so on a build without Firebase configured
 *   nothing of the SDK is ever fetched;
 * - the import is a dynamic `import()`, never a static one, so Rollup gives it
 *   its own chunk rather than folding it into `main`.
 *   `client/src/lib/__tests__/mum-hub-firebase-bundle.test.ts` fails if a
 *   static `firebase/*` import appears anywhere under `client/src`.
 *
 * ## Every read fails closed
 *
 * A missing config, a throw out of the SDK, an unsupported browser, a
 * rejected fetch, a value the console has never heard of, an allow-list that
 * is not a JSON array of strings — every one of them resolves to
 * `{ globalEnabled: false, allowlist: [] }`, which admits nobody. There is no
 * path through this file on which an error widens access.
 *
 * ## Once per page load
 *
 * `fetchAndActivate` is called at most once and its promise is reused, the
 * same shape the app's `primeRemoteConfig` uses. The customer's own token is
 * computed separately and combined at the call site, so an account switch
 * re-decides without re-fetching.
 */

/** What the gate needs, and all it is allowed to know. */
export interface MumHubRemoteConfigValues {
  globalEnabled: boolean;
  allowlist: string[];
}

/** Admits nobody. Every failure path returns exactly this. */
export const MUM_HUB_REMOTE_CONFIG_CLOSED: MumHubRemoteConfigValues = {
  globalEnabled: MUM_HUB_FLAG_DEFAULT,
  allowlist: [],
};

/**
 * The three variables that identify the Firebase Web app. All public: an
 * `apiKey` here is a project identifier, not a credential, and is readable in
 * any Firebase web app's shipped JS. They are still declared in `.env.example`
 * like every other `VITE_` name, because the build's undeclared-VITE_ guard
 * exists so that adding a name to the public bundle is a reviewable act.
 *
 * There is no `measurementId`: that is Analytics, which the website does not
 * initialise here, and Remote Config does not need it.
 */
export interface MumHubFirebaseConfig {
  apiKey: string;
  projectId: string;
  appId: string;
}

/**
 * Read the config, or null.
 *
 * ALL THREE or nothing. A partial config is a misconfiguration, and letting
 * the SDK start with two of the three would produce a runtime error inside
 * Firebase rather than the clean "not configured" answer this whole module is
 * built to give. Blank strings count as absent, because `.env.example` is
 * copied to `.env` and ships every one of these declared-but-empty.
 */
export function readMumHubFirebaseConfig(
  env: Record<string, unknown> = import.meta.env as unknown as Record<string, unknown>,
): MumHubFirebaseConfig | null {
  const value = (key: string): string =>
    typeof env[key] === "string" ? (env[key] as string).trim() : "";

  const apiKey = value("VITE_FIREBASE_API_KEY");
  const projectId = value("VITE_FIREBASE_PROJECT_ID");
  const appId = value("VITE_FIREBASE_APP_ID");

  if (!apiKey || !projectId || !appId) return null;
  return { apiKey, projectId, appId };
}

/**
 * The Firebase surface this module uses, named structurally so the tests can
 * substitute it wholesale. Nothing here imports a Firebase type: doing so
 * would put a static `firebase/*` specifier in the module graph, which is the
 * exact thing the bundle test forbids.
 */
export interface RemoteConfigLike {
  settings: { minimumFetchIntervalMillis: number; fetchTimeoutMillis?: number };
  defaultConfig: Record<string, string | number | boolean>;
}

export interface MumHubFirebaseSdk {
  initializeApp: (config: MumHubFirebaseConfig, name?: string) => unknown;
  getRemoteConfig: (app: unknown) => RemoteConfigLike;
  fetchAndActivate: (rc: RemoteConfigLike) => Promise<boolean>;
  getValue: (
    rc: RemoteConfigLike,
    key: string,
  ) => { getSource: () => string; asBoolean: () => boolean; asString: () => string };
  isSupported?: () => Promise<boolean>;
}

/**
 * Load `firebase/app` and `firebase/remote-config`, dynamically.
 *
 * Split into its own exported function so a test can pass a fake in and, more
 * importantly, so a test can assert this is never CALLED when the env is
 * absent. The default implementation is the only place in `client/src` that
 * names a `firebase/*` specifier, and it names it inside `import()`.
 */
export async function loadFirebaseSdk(): Promise<MumHubFirebaseSdk> {
  const [app, remoteConfig] = await Promise.all([
    import("firebase/app"),
    import("firebase/remote-config"),
  ]);
  return {
    initializeApp: app.initializeApp as MumHubFirebaseSdk["initializeApp"],
    getRemoteConfig: remoteConfig.getRemoteConfig as unknown as MumHubFirebaseSdk["getRemoteConfig"],
    fetchAndActivate: remoteConfig.fetchAndActivate as unknown as MumHubFirebaseSdk["fetchAndActivate"],
    getValue: remoteConfig.getValue as unknown as MumHubFirebaseSdk["getValue"],
    isSupported: remoteConfig.isSupported as MumHubFirebaseSdk["isSupported"],
  };
}

/**
 * Hard cap on one fetch, so a hung socket cannot leave the gate pending for
 * the whole page view. Nothing blocks on it; the gate simply settles closed.
 */
export const MUM_HUB_FETCH_TIMEOUT_MS = 8_000;

/** The interval for this build. `0` in dev so a console flip is verifiable. */
export function mumHubFetchIntervalMs(isDev: boolean): number {
  return isDev ? MUM_HUB_FETCH_TTL_DEV_MS : MUM_HUB_FETCH_TTL_MS;
}

/**
 * Read one activated parameter, with the app's `static` guard.
 *
 * `static` is the SDK saying the key is unknown to it entirely — not even a
 * registered default. Trusting `asBoolean()` there would be fail-OPEN, since
 * an unset key would then be read through whatever the SDK's own zero value
 * happens to be.
 */
function readValues(sdk: MumHubFirebaseSdk, rc: RemoteConfigLike): MumHubRemoteConfigValues {
  let globalEnabled = MUM_HUB_FLAG_DEFAULT;
  try {
    const value = sdk.getValue(rc, MUM_HUB_FLAG_KEY);
    globalEnabled = value.getSource() === "static" ? false : value.asBoolean() === true;
  } catch {
    globalEnabled = false;
  }

  let allowlist: string[] = [];
  try {
    const value = sdk.getValue(rc, MUM_HUB_ALLOWLIST_KEY);
    allowlist = value.getSource() === "static" ? [] : parseMumHubAllowlist(value.asString());
  } catch {
    allowlist = [];
  }

  return { globalEnabled, allowlist };
}

export interface FetchMumHubRemoteConfigOptions {
  config?: MumHubFirebaseConfig | null;
  loadSdk?: () => Promise<MumHubFirebaseSdk>;
  isDev?: boolean;
}

/**
 * Fetch and activate the two parameters. Never rejects, never throws.
 *
 * The `try` wraps the SDK load as well as the fetch, because the load itself
 * is a network request for a chunk and can fail on a flaky connection. A
 * failed chunk load must close the gate, not break the page.
 */
export async function fetchMumHubRemoteConfig(
  options: FetchMumHubRemoteConfigOptions = {},
): Promise<MumHubRemoteConfigValues> {
  const config = options.config === undefined ? readMumHubFirebaseConfig() : options.config;
  // No config, no SDK. Checked before the loader so a build without Firebase
  // never pays for the chunk, and so the bundle cannot grow for a deploy that
  // has not configured it.
  if (!config) return MUM_HUB_REMOTE_CONFIG_CLOSED;

  const loadSdk = options.loadSdk ?? loadFirebaseSdk;
  const isDev = options.isDev ?? Boolean(import.meta.env.DEV);

  try {
    const sdk = await loadSdk();

    // Remote Config needs IndexedDB, which a private window or a locked-down
    // browser may refuse. `isSupported` answers that without throwing.
    if (sdk.isSupported && (await sdk.isSupported()) !== true) {
      return MUM_HUB_REMOTE_CONFIG_CLOSED;
    }

    const rc = sdk.getRemoteConfig(sdk.initializeApp(config));
    rc.settings.minimumFetchIntervalMillis = mumHubFetchIntervalMs(isDev);
    rc.settings.fetchTimeoutMillis = MUM_HUB_FETCH_TIMEOUT_MS;
    // Registered BEFORE the fetch, so that even a fetch that fails leaves the
    // reads below answering from a known, closed default rather than `static`.
    rc.defaultConfig = {
      [MUM_HUB_FLAG_KEY]: MUM_HUB_FLAG_DEFAULT,
      [MUM_HUB_ALLOWLIST_KEY]: MUM_HUB_ALLOWLIST_DEFAULT,
    };

    await sdk.fetchAndActivate(rc);
    return readValues(sdk, rc);
  } catch {
    // Offline, throttled, blocked by an extension, IndexedDB unavailable, a
    // chunk that would not load. All the same answer.
    return MUM_HUB_REMOTE_CONFIG_CLOSED;
  }
}

let inFlight: Promise<MumHubRemoteConfigValues> | null = null;

/**
 * Once per page load, and the promise is shared.
 *
 * The header, the footer, the router and the Account page all ask the same
 * question within one paint; they must not each open a Remote Config fetch.
 * Reusing the promise also means they cannot disagree with each other, the
 * same reason `MUM_HUB_WEB_ENABLED` in `flag.ts` is read once.
 */
export function mumHubRemoteConfigOnce(
  options: FetchMumHubRemoteConfigOptions = {},
): Promise<MumHubRemoteConfigValues> {
  inFlight ??= fetchMumHubRemoteConfig(options);
  return inFlight;
}

/** Test seam. Not for production use. */
export function __resetMumHubRemoteConfigForTests(): void {
  inFlight = null;
}

export { decideMumHubAccess };
